import { computed, ref } from 'vue';
import parseApiErrors from '@/utils/parseApiErrors';
import { useI18n } from '@/composables/useI18n';
import type { FieldErrors } from '@/services/models';
import type { AggregatedErrorItem } from '@/components/global/error-aggregator.vue';

type ApplyApiErrorsOptions = {
  nested?: boolean;
};

export const useApiFieldErrors = () => {
  const { t } = useI18n();
  const fieldErrors = ref<FieldErrors>({});
  const errors = ref<AggregatedErrorItem[]>([]);
  const errorsControlActive = ref(false);

  const errorsDict = computed<Record<string, string[]>>(
    () => fieldErrors.value as Record<string, string[]>,
  );

  const clearErrors = () => {
    fieldErrors.value = {};
    errors.value = [];
    errorsControlActive.value = false;
  };

  const applyApiErrors = (error: unknown, options: ApplyApiErrorsOptions = {}) => {
    fieldErrors.value = parseApiErrors({ error, nested: options.nested });
    const entries = Object.entries(fieldErrors.value);
    if (entries.length === 0) {
      errors.value = [];
      errorsControlActive.value = false;
      return;
    }

    const items: AggregatedErrorItem[] = [{ message: t('Validation failed') }];
    entries.forEach(([field, messages]) => {
      messages.forEach(message => {
        const hasFieldPrefix = message.includes('→');
        const prefixedMessage = hasFieldPrefix ? message : `${t(field)} → ${message}`;
        items.push({ message: prefixedMessage, type: field });
      });
    });

    errors.value = items;
    errorsControlActive.value = errors.value.length > 0;
  };

  return {
    fieldErrors,
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  };
};
